import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function ImmobilierPage() {
  const { t } = useTranslation('navigation');

  const solutions = [
    {
      titleKey: 'immobilierSol1Title',
      descKey: 'immobilierSol1Desc',
      icone: 'ri-building-line',
      fonctionnalites_fr: [
        'Fichier complet des biens immobiliers',
        'Photos et documents associés',
        'Historique des transactions',
        'Évaluation automatique des biens',
        'Cartographie interactive des propriétés'
      ],
      fonctionnalites_en: [
        'Complete property files',
        'Associated photos and documents',
        'Transaction history',
        'Automatic property valuation',
        'Interactive property mapping'
      ]
    },
    {
      titleKey: 'immobilierSol2Title',
      descKey: 'immobilierSol2Desc',
      icone: 'ri-user-line',
      fonctionnalites_fr: [
        'Dossiers locataires complets',
        'Génération automatique des contrats',
        'Suivi des paiements de loyers',
        'Alertes d\'échéances automatiques',
        'Gestion des cautions et garanties'
      ],
      fonctionnalites_en: [
        'Complete tenant files',
        'Automatic contract generation',
        'Rent payment tracking',
        'Automatic deadline alerts',
        'Security deposit management'
      ]
    },
    {
      titleKey: 'immobilierSol3Title',
      descKey: 'immobilierSol3Desc',
      icone: 'ri-calculator-line',
      fonctionnalites_fr: [
        'Facturation automatique des loyers',
        'Gestion des charges locatives',
        'Suivi des impayés',
        'Reporting financier détaillé',
        'Interface comptable intégrée'
      ],
      fonctionnalites_en: [
        'Automatic rent billing',
        'Rental charges management',
        'Unpaid rent tracking',
        'Detailed financial reporting',
        'Integrated accounting interface'
      ]
    },
    {
      titleKey: 'immobilierSol4Title',
      descKey: 'immobilierSol4Desc',
      icone: 'ri-tools-line',
      fonctionnalites_fr: [
        'Planification des interventions',
        'Suivi des demandes locataires',
        'Gestion des prestataires',
        'Historique des travaux',
        'Budget maintenance par bien'
      ],
      fonctionnalites_en: [
        'Intervention planning',
        'Tenant request tracking',
        'Service provider management',
        'Works history',
        'Maintenance budget per property'
      ]
    },
    {
      titleKey: 'immobilierSol5Title',
      descKey: 'immobilierSol5Desc',
      icone: 'ri-smartphone-line',
      fonctionnalites_fr: [
        'Consultation des quittances',
        'Demandes de maintenance en ligne',
        'Paiement en ligne sécurisé',
        'Communication directe',
        'Documents contractuels accessibles'
      ],
      fonctionnalites_en: [
        'Receipt consultation',
        'Online maintenance requests',
        'Secure online payment',
        'Direct communication',
        'Accessible contractual documents'
      ]
    },
    {
      titleKey: 'immobilierSol6Title',
      descKey: 'immobilierSol6Desc',
      icone: 'ri-bar-chart-line',
      fonctionnalites_fr: [
        'Rentabilité par bien',
        'Taux d\'occupation',
        'Évolution des loyers',
        'Prévisions financières',
        'Rapports personnalisables'
      ],
      fonctionnalites_en: [
        'Profitability per property',
        'Occupancy rate',
        'Rent evolution',
        'Financial forecasts',
        'Customizable reports'
      ]
    }
  ];

  const isEn = t('home') === 'Home';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Immobilier - Alpha Solutions | Gestion Locative & Biens"
        description="Solution complète pour agences immobilières et gestionnaires : gestion des biens, locataires, facturation automatique, maintenance et portail propriétaires."
        keywords="logiciel immobilier Cameroun, gestion locative Douala, software immobilier Afrique, gestion biens immobiliers"
        canonicalPath="/secteurs/immobilier"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Immobilier Alpha Solutions",
          "description": "Solution de gestion locative et immobilière au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=modern%20african%20real%20estate%20office%20with%20property%20management%20software%20digital%20portfolio%20display%20professional%20real%20estate%20agency%20contemporary%20technology%20solutions%20property%20management&width=1200&height=600&seq=immobilier-hero-v2&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('immobilierHeroTitle')}</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('immobilierHeroDesc')}</p>
            <a
              href="https://wa.me/237693385267"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap inline-block"
            >
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
            </a>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('immobilierSolutionsTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('immobilierSolutionsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((sol, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-slate-700 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t(sol.titleKey)}</h3>
                <p className="text-gray-600 mb-4">{t(sol.descKey)}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.fonctionnalites_en : sol.fonctionnalites_fr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('immobilierWhyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-time-line', title: t('immobilierAdv1Title'), desc: t('immobilierAdv1Desc') },
              { icon: 'ri-shield-check-line', title: t('immobilierAdv2Title'), desc: t('immobilierAdv2Desc') },
              { icon: 'ri-customer-service-line', title: t('immobilierAdv3Title'), desc: t('immobilierAdv3Desc') },
              { icon: 'ri-money-dollar-circle-line', title: t('immobilierAdv4Title'), desc: t('immobilierAdv4Desc') },
            ].map((adv, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-slate-700 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{adv.title}</h3>
                <p className="text-gray-600">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignage Client */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-lg p-8 border-l-4 border-slate-700">
            <div className="flex items-center mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20african%20real%20estate%20investor%20property%20manager%20in%20modern%20office%20confident%20business%20owner%20managing%20successful%20real%20estate%20portfolio%20with%20digital%20technology&width=100&height=100&seq=sci-platinum-v2&orientation=squarish"
                alt={t('immobilierTestiName')}
                className="w-16 h-16 rounded-full object-cover mr-4"
              />
              <div>
                <h3 className="text-xl font-bold text-slate-900">{t('immobilierTestiName')}</h3>
                <p className="text-slate-700">{t('immobilierTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-slate-800 italic mb-4">
              "{t('immobilierTestiQuote')}"
            </blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}
              </div>
              <span className="ml-2 text-slate-700 font-medium">{t('immobilierTestiSince')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('immobilierCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('immobilierCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-slate-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              {t('sectorViewSolutions')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
